/**
 * Service A Status Utilities
 * 
 * Centralized status labels, colors, and operator guidance.
 * This is the single source of truth for Service A workflow states.
 */

/**
 * Operator-friendly status labels
 * Raw state → Plain English description
 */
export const STATUS_LABELS = {
  // Pre-agreement states
  ENTITLED: "Service Available – Not Yet Accepted",
  AWAITING_AGREEMENT: "Awaiting Agreement Acceptance",
  
  // Post-agreement, pre-payment
  AGREEMENT_ACCEPTED: "Agreement Accepted – Awaiting Payment",
  
  // Post-payment states
  PAID: "Payment Confirmed – Ready to Begin",
  QUESTIONNAIRE_READY: "Interview Prep Ready",
  
  // Evidence collection
  INTERVIEWING: "Interviews In Progress",
  
  // Evidence complete
  EVIDENCE_COMPLETE: "All Evidence Collected – Ready for Analysis",
  
  // Report states
  REPORT_READY: "Report Generated – Pending Review",
  REPORT_ISSUED: "Report Issued to Client",
  
  // Terminal states
  COMPLETE: "Complete – Obligations Fulfilled",
  CLOSED: "Engagement Closed",
  CANCELLED: "Cancelled"
};

/**
 * Status badge colors (Tailwind classes)
 */
export const STATUS_COLORS = {
  // Pre-agreement (gray)
  ENTITLED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  AWAITING_AGREEMENT: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  
  // Post-agreement (amber)
  AGREEMENT_ACCEPTED: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  
  // Post-payment (blue)
  PAID: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  QUESTIONNAIRE_READY: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  
  // Evidence collection (purple)
  INTERVIEWING: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  
  // Evidence complete (green)
  EVIDENCE_COMPLETE: "bg-green-500/20 text-green-400 border-green-500/30",
  
  // Report states (green)
  REPORT_READY: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  REPORT_ISSUED: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  
  // Terminal (green/gray)
  COMPLETE: "bg-green-500/20 text-green-400 border-green-500/30",
  CLOSED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  CANCELLED: "bg-red-500/20 text-red-400 border-red-500/30"
};

/**
 * Get operator-friendly label for a status
 * Includes fallback for unknown states
 */
export const getStatusLabel = (status) => {
  if (!status) return "Status Unknown";
  return STATUS_LABELS[status] || `Status: Needs Review (${status})`;
};

/**
 * Get badge color classes for a status
 * Includes fallback for unknown states
 */
export const getStatusColor = (status) => {
  if (!status) return "bg-red-500/20 text-red-400 border-red-500/30";
  return STATUS_COLORS[status] || "bg-red-500/20 text-red-400 border-red-500/30";
};

/**
 * Timeline event types for structured logging
 */
export const EVENT_TYPES = {
  STATE_TRANSITION: "STATE_TRANSITION",
  AGREEMENT_ACCEPTED: "AGREEMENT_ACCEPTED",
  PAYMENT_SUCCEEDED: "PAYMENT_SUCCEEDED",
  PAYMENT_FAILED: "PAYMENT_FAILED",
  QUESTIONNAIRE_GENERATED: "QUESTIONNAIRE_GENERATED",
  INTERVIEW_SCHEDULED: "INTERVIEW_SCHEDULED",
  INTERVIEW_COMPLETED: "INTERVIEW_COMPLETED",
  TRANSCRIPT_UPLOADED: "TRANSCRIPT_UPLOADED",
  REPORT_GENERATED: "REPORT_GENERATED",
  REPORT_ISSUED: "REPORT_ISSUED"
};

/**
 * Human-readable labels for timeline events
 */
export const EVENT_LABELS = {
  STATE_TRANSITION: "Status Changed",
  AGREEMENT_ACCEPTED: "Agreement Accepted",
  PAYMENT_SUCCEEDED: "Payment Confirmed",
  PAYMENT_FAILED: "Payment Failed",
  QUESTIONNAIRE_GENERATED: "Interview Questionnaire Generated",
  INTERVIEW_SCHEDULED: "Interview Scheduled",
  INTERVIEW_COMPLETED: "Interview Completed",
  TRANSCRIPT_UPLOADED: "Transcript Uploaded",
  REPORT_GENERATED: "Final Report Generated",
  REPORT_ISSUED: "Report Issued to Client"
};

/**
 * Get timeline event label
 */
export const getEventLabel = (eventType) => {
  return EVENT_LABELS[eventType] || eventType;
};

/**
 * Convert lifecycle history + activity data to timeline events
 * @param {Object} ser - Service Execution Record
 * @param {Array} interviews - Interview records
 * @param {Array} artifacts - Artifact records
 * @returns {Array} Sorted timeline events
 */
export const buildTimeline = (ser, interviews = [], artifacts = []) => {
  const events = [];
  
  // Add lifecycle history transitions
  if (ser?.lifecycle_history) {
    ser.lifecycle_history.forEach((entry) => {
      events.push({
        timestamp: entry.changed_at,
        event_type: EVENT_TYPES.STATE_TRANSITION,
        label: entry.to_state 
          ? `Status: ${getStatusLabel(entry.to_state)}`
          : "Engagement Created",
        actor: entry.changed_by?.toLowerCase() || "system",
        metadata: {
          from_state: entry.from_state,
          to_state: entry.to_state
        }
      });
    });
  }
  
  // Add agreement accepted event
  if (ser?.agreement?.accepted_at) {
    events.push({
      timestamp: ser.agreement.accepted_at,
      event_type: EVENT_TYPES.AGREEMENT_ACCEPTED,
      label: "Agreement Accepted",
      actor: "admin",
      metadata: { version: ser.agreement.version }
    });
  }
  
  // Add payment event
  if (ser?.payment?.paid_at) {
    events.push({
      timestamp: ser.payment.paid_at,
      event_type: EVENT_TYPES.PAYMENT_SUCCEEDED,
      label: `Payment Confirmed ($${(ser.payment.amount_cents / 100).toLocaleString()})`,
      actor: ser.payment.provider === "mock" ? "admin (mock)" : "stripe",
      metadata: {
        amount_cents: ser.payment.amount_cents,
        provider: ser.payment.provider
      }
    });
  }
  
  // Add questionnaire generated event
  if (ser?.questionnaire?.generated_at) {
    events.push({
      timestamp: ser.questionnaire.generated_at,
      event_type: EVENT_TYPES.QUESTIONNAIRE_GENERATED,
      label: `Interview Questionnaire Generated (v${ser.questionnaire.version})`,
      actor: "system",
      metadata: {
        version: ser.questionnaire.version,
        artifact_id: ser.questionnaire.artifact_id
      }
    });
  }
  
  // Add interview events
  interviews.forEach((interview) => {
    if (interview.scheduled_start) {
      events.push({
        timestamp: interview.scheduled_start,
        event_type: EVENT_TYPES.INTERVIEW_SCHEDULED,
        label: `Interview ${interview.ordinal} Scheduled`,
        actor: "admin",
        metadata: {
          interview_id: interview.id,
          ordinal: interview.ordinal
        }
      });
    }
    if (interview.completed_at) {
      events.push({
        timestamp: interview.completed_at,
        event_type: EVENT_TYPES.INTERVIEW_COMPLETED,
        label: `Interview ${interview.ordinal} Completed`,
        actor: "admin",
        metadata: {
          interview_id: interview.id,
          ordinal: interview.ordinal
        }
      });
    }
  });
  
  // Add transcript upload events
  artifacts
    .filter(a => a.type === "transcript")
    .forEach((artifact) => {
      events.push({
        timestamp: artifact.created_at,
        event_type: EVENT_TYPES.TRANSCRIPT_UPLOADED,
        label: `Transcript Uploaded (Interview ${artifact.metadata?.ordinal || "?"})`,
        actor: "admin",
        metadata: {
          artifact_id: artifact.id,
          interview_id: artifact.metadata?.interview_id,
          filename: artifact.filename
        }
      });
    });
  
  // Sort by timestamp descending (most recent first)
  return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

/**
 * Format timestamp for display
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true
  });
};
